import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MagnifyingGlassIcon, MapPinIcon, ClockIcon, ArchiveBoxIcon, TruckIcon } from '@heroicons/react/24/outline';
import LanguageSwitcher from '../components/ui/LanguageSwitcher';

const TrackingPage = () => {
  const { t } = useTranslation();
  const { trackingId: urlTrackingId } = useParams();
  const [trackingId, setTrackingId] = useState(urlTrackingId || '');
  const [trackingInfo, setTrackingInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleTrack = () => {
    if (!trackingId) return;
    
    setLoading(true);
    
    // Mock tracking data
    setTimeout(() => {
      setTrackingInfo({
        id: trackingId,
        status: 'in-transit',
        origin: 'New York, NY',
        destination: 'Los Angeles, CA',
        currentLocation: 'Chicago, IL',
        estimatedDelivery: '2024-01-25',
        progress: 65,
        timeline: [
          { status: 'picked-up', location: 'New York, NY', time: '2024-01-20 08:00 AM', completed: true },
          { status: 'in-transit', location: 'Chicago, IL', time: '2024-01-22 02:30 PM', completed: true },
          { status: 'out-for-delivery', location: 'Los Angeles, CA', time: 'Estimated 2024-01-25 09:00 AM', completed: false },
          { status: 'delivered', location: 'Los Angeles, CA', time: 'Estimated 2024-01-25 12:00 PM', completed: false }
        ]
      });
      setLoading(false);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2">
              <TruckIcon className="w-8 h-8 text-primary-600" />
              <span className="text-xl font-bold text-gray-900">Logistiat</span>
            </div>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {t('tracking.title')}
          </h1>
          <p className="text-gray-600">
            Enter your tracking ID to see the current status of your shipment
          </p>
        </div>

        {/* Tracking Input */}
        <div className="card mb-8">
          <div className="card-body">
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <div className="flex-1">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={trackingId}
                    onChange={(e) => setTrackingId(e.target.value)}
                    placeholder={t('tracking.enterTrackingId')}
                    className="input pl-10 text-lg"
                    onKeyPress={(e) => e.key === 'Enter' && handleTrack()}
                  />
                </div>
              </div>
              <button
                onClick={handleTrack}
                disabled={!trackingId || loading}
                className="btn btn-primary btn-lg px-8"
              >
                {loading ? 'Tracking...' : 'Track'}
              </button>
            </div>
          </div>
        </div>

        {/* Tracking Results */}
        {trackingInfo && (
          <div className="space-y-6">
            {/* Status Overview */}
            <div className="card">
              <div className="card-body">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <ArchiveBoxIcon className="w-12 h-12 text-primary-600 mx-auto mb-2" />
                    <h3 className="font-semibold text-gray-900">Tracking ID</h3>
                    <p className="text-gray-600">{trackingInfo.id}</p>
                  </div>
                  <div className="text-center">
                    <MapPinIcon className="w-12 h-12 text-green-600 mx-auto mb-2" />
                    <h3 className="font-semibold text-gray-900">{t('tracking.currentLocation')}</h3>
                    <p className="text-gray-600">{trackingInfo.currentLocation}</p>
                  </div>
                  <div className="text-center">
                    <ClockIcon className="w-12 h-12 text-blue-600 mx-auto mb-2" />
                    <h3 className="font-semibold text-gray-900">{t('tracking.estimatedArrival')}</h3>
                    <p className="text-gray-600">{trackingInfo.estimatedDelivery}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="card">
              <div className="card-body">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t('tracking.shipmentProgress')}
                </h3>
                <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                  <div 
                    className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${trackingInfo.progress}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-600 text-center">
                  {trackingInfo.progress}% Complete • From {trackingInfo.origin} to {trackingInfo.destination}
                </p>
              </div>
            </div>

            {/* Timeline */}
            <div className="card">
              <div className="card-body">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Tracking Timeline</h3>
                <div className="space-y-4">
                  {trackingInfo.timeline.map((event, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className={`w-4 h-4 rounded-full mt-1 ${
                        event.completed ? 'bg-green-500' : 'bg-gray-300'
                      }`}></div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className={`font-medium ${
                            event.completed ? 'text-gray-900' : 'text-gray-500'
                          }`}>
                            {event.status.split('-').map(word => 
                              word.charAt(0).toUpperCase() + word.slice(1)
                            ).join(' ')}
                          </h4>
                          <span className={`text-sm ${
                            event.completed ? 'text-gray-600' : 'text-gray-400'
                          }`}>
                            {event.time}
                          </span>
                        </div>
                        <p className={`text-sm ${
                          event.completed ? 'text-gray-600' : 'text-gray-400'
                        }`}>
                          {event.location}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* No Results */}
        {trackingId && !trackingInfo && !loading && (
          <div className="card">
            <div className="card-body text-center py-12">
              <ArchiveBoxIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t('tracking.trackingNotFound')}
              </h3>
              <p className="text-gray-500">
                Please check your tracking ID and try again.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default TrackingPage; 